import React, { createContext, useState, useContext, useEffect, useMemo } from "react";
import axios from "axios";
import { getToken } from "../utils/tokenUtils";

// Create the EmploymentTypeMasterContext
const ShiftTimingMasterContext = createContext();

// Custom hook to use the EmploymentTypeMasterContext
export const useShiftTiming = () => useContext(ShiftTimingMasterContext);

// Provider component to wrap the app
export const ShiftTimingMasterProvider = ({ children }) => {
  const [shiftTimings, setShiftTimings] = useState([]);
  const API_URL = "https://hrmsnew.prepstripe.com/api/masters/shift-timings"; // Replace with your actual API URL
  const token = getToken();
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    perPage: 10,
  });
  const [isLoading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchShiftTimings = async (page = 1) => {
    try {
      setLoading(true);
      const response = await axios.get(API_URL, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const { data, current_page, last_page, per_page, total } = response.data;

      setShiftTimings(data || []); // Update leave types list

      setPagination({
          currentPage: current_page,
          totalPages: last_page,
          totalItems: total,
          perPage: per_page,
      });
      setLoading(false);
      // setShiftTimings(response.data?.data || {}); // Access `data.data` for actual leave master
    } catch (error) {
      console.error("Error fetching shift-timing master:", error);
      setError(error.response?.data?.message || "Failed to fetch shift timing requests.");
    } finally {
      setLoading(false);
    }
  };

  // Fetch departments master from the API on load
  useEffect(() => {
    fetchShiftTimings();
  }, [API_URL, token]);

  // Add a new employment-type
  const addShiftTiming = async (newShiftTiming) => {
    try {
      const response = await axios.post(API_URL, newShiftTiming, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setShiftTimings((prev) => [response?.data, ...prev]);
    } catch (error) {
      console.error("Error adding shift-timing:", error);
    }
  };

  // Update an existing employment-type
  const updateShiftTiming = async (updatedShiftTiming) => {
    try {
      const response = await axios.put(`${API_URL}/${updatedShiftTiming.id}`, updatedShiftTiming, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setShiftTimings((prev) =>
        prev.map((shiftTiming) =>
            shiftTiming.id === updatedShiftTiming.id ? { ...shiftTiming, ...response.data } : shiftTiming
        )
      );
    } catch (error) {
      console.error("Error updating shift-timing master:", error);
    }
  };

  // Delete a leave-type
  const deleteShiftTiming = async (id) => {
    try {
      await axios.delete(`${API_URL}/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setShiftTimings((prev) => prev.filter((shiftTiming) => shiftTiming.id !== id));
    } catch (error) {
      console.error("Error deleting shift-timing:", error);
    }
  };

  // Memoize the context value
  const value = useMemo(
    () => ({
      isLoading,
      error,
      pagination,
      shiftTimings,
      addShiftTiming,
      updateShiftTiming,
      deleteShiftTiming,
    }),
    [shiftTimings, isLoading] // Recalculate the value only when `employment-type` changes
  );

  return <ShiftTimingMasterContext.Provider value={value}>{children}</ShiftTimingMasterContext.Provider>;
};
